import { flow } from 'fp-ts/function';
import { filter, get, identity, isEmpty, lt, mapValues, pick, size } from 'lodash/fp';
import { createSelector } from 'reselect';

import { TextKey } from '@mews-distributor/common';

import { hasCurrentDateEnterpriseAvailableCategories } from '../../selectors/availability';
import { parentSelector } from '../../selectors/parentSelector';
import { getSelectedCityEnterprisesIds } from '../../selectors/views/selectedCityEnterprises';
import {
    getSelectedEnterpriseCityId,
    getSelectedEnterpriseIntroImageId,
} from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { ApplicationState } from '../../types';
import { getSelectedCityId } from '../app/selectors';
import { localizeText } from '../globalization/selectors';
import { getCategoryGalleryImage, getSortedCategoryGalleryImages } from '../resourceCategories/selectors';
import { getSelectedHotelId } from '../resourceOrder/selectors';

const getHotels = createSelector(parentSelector, get('hotels'));

export const getSelectedCityHotelsWithAvailableRooms = (state: ApplicationState) => {
    const enterpriseIds = getSelectedCityEnterprisesIds(state, {});
    return filter(
        (enterpriseId) => hasCurrentDateEnterpriseAvailableCategories(state, { enterpriseId }),
        enterpriseIds
    );
};

export const hasSelectedCityPropertyAvailableRooms = flow(getSelectedCityHotelsWithAvailableRooms, size, lt(0));

const getSelectedHotel = createSelector(getSelectedHotelId, getHotels, get);

export const getSelectedHotelRates = createSelector(getSelectedHotel, (hotel) => hotel?.rates);
export const getSelectedHotelRateGroups = createSelector(getSelectedHotel, (hotel) => hotel?.rateGroups);

export const isSelectedHotelCitySelected = createSelector(
    getSelectedEnterpriseCityId,
    getSelectedCityId,
    (hotelCityId, cityId) => hotelCityId === cityId
);

export const getViewRates = createSelector(
    getSelectedHotelRates,
    mapValues(pick(['id', 'name', 'description', 'isPrivate', 'rateGroupId', 'ordering']))
);

export const getCategoryGalleryImagesWithFallback = createSelector(
    identity,
    getSortedCategoryGalleryImages,
    getSelectedEnterpriseIntroImageId,
    (state, images, fallbackImage) =>
        // @ts-expect-error $TSFixMeError: Argument of type 'undefined' is not assignable to parameter of type 'ApplicationState'.
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        isEmpty(images) && fallbackImage ? [getCategoryGalleryImage(state, { imageId: fallbackImage })] : images
);

export const getCarouselWrapperAriaLabel = (state: ApplicationState) => localizeText(TextKey.Gallery)(state);
export const getCarouselCloseIconButtonLabel = (state: ApplicationState) => localizeText(TextKey.Close)(state);
export const getCarouselNextIconButtonLabel = (state: ApplicationState) => localizeText(TextKey.NextImage)(state);
export const getCarouselPrevIconButtonLabel = (state: ApplicationState) => localizeText(TextKey.PreviousImage)(state);
